import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, MapPin } from "lucide-react";
import { TESTIMONIALS } from "../data/siteData";
import Reveal from "./Reveal";
import { SectionHead } from "./StatsBar";

export default function Testimonials({ filterCity = null, title, sub }) {
  const list = filterCity
    ? TESTIMONIALS.filter((t) => t.city.toLowerCase() === filterCity.toLowerCase()).concat(
        TESTIMONIALS.filter((t) => t.city.toLowerCase() !== filterCity.toLowerCase()).slice(0, 3)
      )
    : TESTIMONIALS;
  const [idx, setIdx] = useState(0);
  const next = useCallback(() => setIdx((i) => (i + 1) % list.length), [list.length]);
  const prev = () => setIdx((i) => (i - 1 + list.length) % list.length);

  useEffect(() => {
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, [next, idx]);

  const visible = [list[idx], list[(idx + 1) % list.length], list[(idx + 2) % list.length]];

  return (
    <section data-testid="testimonials-section" className="relative py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          eyebrow="Client Success"
          title={title || "Founders who stopped worrying about compliance"}
          sub={sub || "Real engagements, measurable outcomes — from incorporation desks to signed term sheets."}
        />
        <div className="relative">
          <AnimatePresence mode="popLayout">
            <div className="grid gap-6 md:grid-cols-3">
              {visible.map((t, i) => (
                <motion.figure
                  key={`${t.name}-${idx}-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: i === 0 ? 1 : 0.85, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`glass card-lift rounded-3xl p-7 ${i > 0 ? "hidden md:block" : ""}`}
                  data-testid={`testimonial-card-${i}`}
                >
                  <Quote className="mb-4 h-7 w-7 text-brand-blue/40" />
                  <blockquote className="text-sm leading-relaxed text-brand-navy/80">{t.quote}</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-brand-blue/10 pt-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-brand-blue to-brand-sky font-display text-sm font-bold text-white">
                      {t.name.split(" ").map((n) => n[0]).join("")}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-brand-navy">{t.name}</p>
                      <p className="text-xs text-brand-gray">{t.role}, {t.company}</p>
                      <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-brand-blue"><MapPin className="h-3 w-3" /> {t.city}</p>
                    </div>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </AnimatePresence>
          <div className="mt-8 flex items-center justify-center gap-3">
            <button data-testid="testimonial-prev" onClick={prev} aria-label="Previous testimonials" className="glass flex h-10 w-10 items-center justify-center rounded-full text-brand-navy transition-colors hover:bg-brand-ice">
              <ChevronLeft className="h-5 w-5" />
            </button>
            {list.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => setIdx(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-brand-blue" : "w-2 bg-brand-blue/25"}`}
              />
            ))}
            <button data-testid="testimonial-next" onClick={next} aria-label="Next testimonials" className="glass flex h-10 w-10 items-center justify-center rounded-full text-brand-navy transition-colors hover:bg-brand-ice">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export { Reveal };
